import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Key, ShieldCheck, Smartphone, CheckCircle2 } from "lucide-react";
import { Brand } from "../components/Brand";

/**
 * Pagina obbligatoria di primo-accesso: cambio password + enrollment TOTP.
 * Protetta dal cookie di sessione; se non c'è → /login.
 * Legge /auth/me per capire cosa manca e disegna lo stepper di conseguenza.
 */
export default function AccountSetup() {
  const nav = useNavigate();
  const { user, ready, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // step 1: cambio password
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [pwdDone, setPwdDone] = useState(false);

  // step 2: TOTP
  const [enroll, setEnroll] = useState(null); // {secret, qr_svg, uri}
  const [totpCode, setTotpCode] = useState("");
  const [twoFaDone, setTwoFaDone] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        setMe(r.data);
        setPwdDone(!r.data.must_change_password);
        setTwoFaDone(!!r.data.two_factor_enabled || !r.data.two_factor_required);
      })
      .catch(() => nav("/login", { replace: true }))
      .finally(() => setLoading(false));
  }, [nav]);

  // avvia setup TOTP automaticamente quando serve
  useEffect(() => {
    if (!me) return;
    if (pwdDone && me.two_factor_required && !me.two_factor_enabled && !enroll) {
      api.post("/auth/totp/setup", {})
        .then((r) => setEnroll(r.data))
        .catch((e) => toast.error(e?.response?.data?.detail || "Setup 2FA fallito"));
    }
  }, [me, pwdDone, enroll]);

  const submitPwd = async (e) => {
    e.preventDefault();
    if (newPwd !== newPwd2) return toast.error("Le password non coincidono");
    if (newPwd.length < 12) return toast.error("Minimo 12 caratteri");
    if (!/[A-Z]/.test(newPwd) || !/[a-z]/.test(newPwd) || !/\d/.test(newPwd))
      return toast.error("Deve contenere maiuscole, minuscole e almeno una cifra");
    try {
      // Il backend valida la vecchia pwd — qui la chiediamo implicitamente al secondo login.
      // In questa route lo scope del token è "restricted", ma /auth/change-password accetta
      // solo la nuova password se must_change_password è true e la sessione è già autenticata:
      await api.post("/auth/change-password", { old_password: "__forced__", new_password: newPwd, forced: true });
      toast.success("Password aggiornata");
      setPwdDone(true);
      // ricarica il profilo
      const r = await api.get("/auth/me");
      setMe(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cambio password fallito");
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/totp/confirm", { code: totpCode });
      toast.success("2FA attivato");
      setTwoFaDone(true);
      // ricarica profilo (il token resta restricted finché l'utente non rifà login)
      try { await api.post("/auth/logout", {}); } catch {}
      try { localStorage.removeItem("fabri_user"); } catch {}
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice errato");
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#050505] text-white/60">Caricamento…</div>;
  }

  const stepPwd = !pwdDone;
  const step2fa = pwdDone && me?.two_factor_required && !twoFaDone;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 overflow-hidden bg-[#050505]" data-testid="account-setup-page">
      {/* Left side */}
      <div className="hidden lg:flex relative bg-black items-center justify-center">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 text-center max-w-md px-8">
          <ShieldCheck size={80} className="mx-auto text-[#ff0033]" strokeWidth={1.3} />
          <div className="font-display text-4xl font-black tracking-tighter uppercase mt-8">
            <span className="neon-text">Primo Accesso</span>
          </div>
          <div className="text-xs uppercase tracking-[0.4em] text-white/40 mt-4">
            Imposta credenziali definitive
          </div>

          <div className="mt-10 space-y-4 text-left">
            <StepRow label="Nuova password" done={pwdDone} active={stepPwd} />
            {me?.two_factor_required && (
              <StepRow label="Attiva 2FA" done={twoFaDone} active={step2fa} />
            )}
          </div>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm relative z-10">
          <Brand />
          {stepPwd && (
            <>
              <div className="flex items-center gap-2 mt-10">
                <Key size={22} className="text-[#ff0033]" />
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Nuova password</h1>
              </div>
              <p className="text-sm text-white/45 mt-2">Minimo 12 caratteri · 1 maiuscola · 1 minuscola · 1 numero</p>
              <form onSubmit={submitPwd} className="mt-8 space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/45">Password</label>
                  <input data-testid="setup-pwd-1" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={12}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/45">Conferma password</label>
                  <input data-testid="setup-pwd-2" type="password" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} required minLength={12}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
                </div>
                <button data-testid="setup-pwd-submit" className="btn-neon w-full py-3.5 rounded-xl text-sm">Imposta password</button>
              </form>
            </>
          )}

          {step2fa && (
            <>
              <div className="flex items-center gap-2 mt-10">
                <Smartphone size={22} className="text-[#ff0033]" />
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Attiva 2FA</h1>
              </div>
              <p className="text-sm text-white/45 mt-2">Scansiona il QR con Google Authenticator / Authy e inserisci il codice.</p>
              {enroll ? (
                <>
                  <div className="mt-6 p-4 rounded-xl bg-white border border-white/10 flex justify-center" data-testid="setup-qr">
                    <div dangerouslySetInnerHTML={{ __html: enroll.qr_svg || "" }} />
                  </div>
                  <form onSubmit={submitTotp} className="mt-6 space-y-4">
                    <input data-testid="setup-totp-code" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus placeholder="Codice a 6 cifre"
                      value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0,6))} required
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono focus:border-[#ff0033]/60 focus:outline-none" />
                    <button data-testid="setup-totp-submit" className="btn-neon w-full py-3.5 rounded-xl text-sm">Attiva 2FA</button>
                  </form>
                </>
              ) : (
                <div className="mt-10 text-sm text-white/50">Generazione QR in corso…</div>
              )}
            </>
          )}

          {!stepPwd && !step2fa && (
            <>
              <div className="flex items-center gap-2 mt-10">
                <CheckCircle2 size={22} className="text-emerald-400" />
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight">Setup completato</h1>
              </div>
              <p className="text-sm text-white/50 mt-3">Rifai il login con le tue credenziali definitive.</p>
              <button data-testid="setup-go-login" onClick={async () => {
                try { await api.post("/auth/logout", {}); } catch {}
                try { localStorage.removeItem("fabri_user"); } catch {}
                nav("/login", { replace: true });
              }} className="btn-neon w-full py-3.5 rounded-xl text-sm mt-6">Vai al login</button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const StepRow = ({ label, done, active }) => (
  <div className={`flex items-center gap-3 ${active ? "opacity-100" : done ? "opacity-70" : "opacity-30"}`}>
    <div className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${done ? "bg-emerald-500 text-black" : active ? "bg-[#ff0033] text-black" : "bg-white/10 text-white/60"}`}>
      {done ? "✓" : "•"}
    </div>
    <div className="text-sm uppercase tracking-widest">{label}</div>
  </div>
);
